// src/components/SnapshotsView.tsx
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input, Progress } from '@/components/ui';
import { useSnapshotHistorial, useUltimoSnapshot } from '@/hooks/useSnapshots';
import { calcularEstadisticas, CALIFICACION_COLORS, CALIFICACION_LABELS, compararSnapshots } from '@/utils/snapshotHelpers';
import { Calendar, RefreshCw, TrendingUp } from 'lucide-react';
import { useState } from 'react';

interface SnapshotsViewProps {
  nit: string;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function SnapshotCard({ snapshot, isLatest = false }: { snapshot: any; isLatest?: boolean }) {
  const stats = calcularEstadisticas(snapshot.estadosRequisitos);

  return (
    <Card className={`hover:shadow-md transition-shadow ${isLatest ? 'ring-2 ring-teal-200' : ''}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {formatDate(snapshot.fechaDia)}
            {isLatest && <Badge variant="blue">Más reciente</Badge>}
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-teal-600">{stats.porcentajeCumplimiento.toFixed(1)}%</div>
            <div className="text-xs text-gray-500">Cumplimiento</div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardBody>
        <Progress value={stats.porcentajeCumplimiento} size="sm" className="mb-4" />

        <div className="grid grid-cols-5 gap-2 text-xs">
          <div className="bg-green-50 rounded p-2 text-center">
            <div className={`font-semibold ${CALIFICACION_COLORS.C}`}>C</div>
            <div className="text-gray-600">{stats.cumple}</div>
          </div>
          <div className="bg-red-50 rounded p-2 text-center">
            <div className={`font-semibold ${CALIFICACION_COLORS.NC}`}>NC</div>
            <div className="text-gray-600">{stats.noCumple}</div>
          </div>
          <div className="bg-yellow-50 rounded p-2 text-center">
            <div className={`font-semibold ${CALIFICACION_COLORS.CP}`}>CP</div>
            <div className="text-gray-600">{stats.cumpleParcialmente}</div>
          </div>
          <div className="bg-gray-50 rounded p-2 text-center">
            <div className={`font-semibold ${CALIFICACION_COLORS.NA}`}>NA</div>
            <div className="text-gray-600">{stats.noAplica}</div>
          </div>
          <div className="bg-blue-50 rounded p-2 text-center">
            <div className="font-semibold text-blue-600">Total</div>
            <div className="text-gray-600">{stats.total}</div>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-500">Requisitos aplicables: {stats.aplicables}</div>
      </CardBody>
    </Card>
  );
}

function ComparacionSnapshots({ snapshots }: { snapshots: any[] }) {
  if (snapshots.length < 2) return null;

  const sortedSnapshots = [...snapshots].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const masReciente = sortedSnapshots[0];
  const anterior = sortedSnapshots[1];

  const cambios = compararSnapshots(anterior.estadosRequisitos, masReciente.estadosRequisitos);

  if (cambios.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-4">
            <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No hay cambios desde el snapshot anterior</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Cambios desde {formatDate(anterior.fechaDia)}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="space-y-2">
          {cambios.slice(0, 10).map((cambio) => (
            <div key={cambio.requisitoId} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">Requisito {cambio.requisitoId}</span>
              </div>
              <div className="flex items-center gap-2">
                {cambio.estadoAnterior && (
                  <Badge variant="default" className={CALIFICACION_COLORS[cambio.estadoAnterior]}>
                    {CALIFICACION_LABELS[cambio.estadoAnterior]}
                  </Badge>
                )}
                <span className="text-gray-400">→</span>
                {cambio.estadoActual && (
                  <Badge variant="default" className={CALIFICACION_COLORS[cambio.estadoActual]}>
                    {CALIFICACION_LABELS[cambio.estadoActual]}
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {cambios.length > 10 && <p className="text-xs text-gray-500 text-center pt-2">Y {cambios.length - 10} cambios más...</p>}
        </div>
      </CardBody>
    </Card>
  );
}

export function SnapshotsView({ nit }: SnapshotsViewProps) {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const {
    data: historial,
    isLoading: loadingHistorial,
    error: errorHistorial,
    refetch: refetchHistorial,
  } = useSnapshotHistorial(nit, {
    fechaInicio: fechaInicio || undefined,
    fechaFin: fechaFin || undefined,
  });
  const {
    data: ultimoSnapshot,
    isLoading: loadingUltimo,
    error: errorUltimo,
    refetch: refetchUltimo,
  } = useUltimoSnapshot(nit, Boolean(historial && historial.length > 0));

  const handleRefresh = () => {
    if (historial && historial.length > 0) {
      refetchUltimo();
    }
    refetchHistorial();
  };

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Historial de Estados</h3>
          <p className="text-sm text-gray-500">Seguimiento del cumplimiento a lo largo del tiempo</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loadingHistorial || loadingUltimo}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistorial || loadingUltimo ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
              <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="w-full" />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
              <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="w-full" />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Latest Snapshot */}
      {ultimoSnapshot && (
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-4">Estado Actual</h4>
          <SnapshotCard snapshot={ultimoSnapshot} isLatest />
        </div>
      )}

      {/* Comparison */}
      {historial && historial.length > 1 && <ComparacionSnapshots snapshots={historial} />}

      {/* Historical Snapshots */}
      {historial && historial.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-4">Historial Completo ({historial.length} snapshots)</h4>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {historial.map((snapshot) => (
              <SnapshotCard key={snapshot.id} snapshot={snapshot} />
            ))}
          </div>
        </div>
      )}

      {/* Loading states */}
      {(loadingUltimo || loadingHistorial) && (
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Cargando snapshots...</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error states */}
      {(errorUltimo || errorHistorial) && (
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <p className="text-red-600">Error al cargar los snapshots</p>
              <p className="text-sm text-gray-500 mt-2">{errorUltimo || errorHistorial}</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Empty state */}
      {!loadingUltimo && !loadingHistorial && !errorUltimo && !errorHistorial && (!historial || historial.length === 0) && (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay snapshots disponibles</h3>
              <p className="text-gray-500">Los snapshots se generan automáticamente durante las asesorías</p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
